<script setup lang="ts">
// Two-axis colour-intensity grid. Both axes are categorical, the value
// drives a colour ramp, and (x, y) cells with no row stay blank rather
// than rendering as zeros — "missing" and "zero" carry different
// meanings and the chart should not lie about which it has.
//
// The host computes the unique x / y categories from `data` (or honours
// `xOrder` / `yOrder` overrides for natural orderings the data can't
// express, e.g. Mon..Sun, hour 0..23) and feeds ECharts a sparse
// `[xIndex, yIndex, value]` triplet per row. Click datums reach back to
// the original row so authors get the whole record, not just the
// reduced triplet.

import { computed } from "vue";

import VChart from "vue-echarts";
import "../echarts.ts";

import type { HeatmapChartSpec } from "../sbi/protocol";
import { CHART_COLORS, COLORS } from "../theme.ts";
import { formatValue } from "../format.ts";
import { baseOption, chartLabelColor, resolveCssVar, tooltipDefaults } from "./echarts-base";
import { categoryLabelPolicy, plotWidthEstimate, verticalCategoryLabel } from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

/** Below this row height (px) y labels thin instead of overlapping. */
const MIN_LABELLED_ROW_PX = 12;

const props = withDefaults(
  defineProps<{
    spec: HeatmapChartSpec;
    /** Field → format registry; `spec.value` is the fallback lookup key. */
    formats?: Record<string, string>;
  }>(),
  {
    formats: () => ({}),
  },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

interface CellModel {
  xCats: ReadonlyArray<string | number>;
  yCats: ReadonlyArray<string | number>;
  // ECharts heatmap expects [xIndex, yIndex, value] triplets.
  cells: ReadonlyArray<[number, number, number]>;
  // Parallel array indexed identically to `cells`, holding the
  // original data row so click datums can return the full record.
  rows: ReadonlyArray<Record<string, unknown>>;
  min: number;
  max: number;
}

function uniqueInOrder<T extends string | number>(values: ReadonlyArray<T>): T[] {
  const seen = new Set<T>();
  const out: T[] = [];
  for (const v of values) {
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

const model = computed<CellModel>(() => {
  const rows = props.spec.data as ReadonlyArray<Record<string, unknown>>;
  const xKey = props.spec.x;
  const yKey = props.spec.y;
  const vKey = props.spec.value;

  const xCats =
    props.spec.xOrder !== undefined
      ? [...props.spec.xOrder]
      : uniqueInOrder(rows.map((r) => r[xKey] as string | number));
  const yCats =
    props.spec.yOrder !== undefined
      ? [...props.spec.yOrder]
      : uniqueInOrder(rows.map((r) => r[yKey] as string | number));

  const xIndex = new Map(xCats.map((c, i) => [c, i] as const));
  const yIndex = new Map(yCats.map((c, i) => [c, i] as const));

  const cells: [number, number, number][] = [];
  const rowAt: Record<string, unknown>[] = [];
  let min = Infinity;
  let max = -Infinity;
  for (const row of rows) {
    const xi = xIndex.get(row[xKey] as string | number);
    const yi = yIndex.get(row[yKey] as string | number);
    if (xi === undefined || yi === undefined) continue;
    const raw = row[vKey];
    const v = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(v)) continue;
    cells.push([xi, yi, v]);
    rowAt.push(row);
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!Number.isFinite(min)) {
    min = 0;
    max = 0;
  }
  return { xCats, yCats, cells, rows: rowAt, min, max };
});

// `sequential` runs from card-tinted floor to brand accent; `diverging`
// pivots through a neutral mid for axes where zero matters (e.g. signed
// deltas). When the data is single-signed `diverging` still works but
// won't visually centre on zero — that's a deliberate caller choice.
//
// Zero-range guard: when every cell shares the same value (or the model
// bailed to `min = max = 0` for an empty set) the raw range collapses.
// ECharts' continuous visualMap then divides by zero when normalising a
// value onto the ramp, drops out of the interpolation path, and the
// series falls through to the top-level categorical `PALETTE` — every
// cell paints as solid `#6aceb0`, which reads as "the heatmap is
// broken." Nudging the ceiling by 1 keeps the ramp anchored at the
// floor colour for constant data.
const visualMap = computed(() => {
  const m = model.value;
  const diverging = props.spec.colorScale === "diverging";
  const rawRange =
    diverging && m.min < 0 && m.max > 0
      ? [-Math.max(Math.abs(m.min), Math.abs(m.max)), Math.max(Math.abs(m.min), Math.abs(m.max))]
      : [m.min, m.max];
  const range =
    rawRange[0]! === rawRange[1]! ? [rawRange[0]!, rawRange[1]! + 1] : rawRange;
  // Ramp endpoints. `spec.lowColor` / `highColor` / `midColor` accept
  // either a `var(--…)` reference OR a raw CSS colour literal (hex /
  // rgb / hsl) — see `HEATMAP_RAMP_COLOR_FIELD` in `chart-spec-schema.ts`
  // for the exact pattern and the rationale for loosening the
  // token-only rule that still applies to `divergent.{positive,
  // negative}Color` and `thresholds[].color`. `resolveCssVar` handles
  // both shapes: it resolves `var(--…)` against the embed host's
  // computed style and passes literals through unchanged. Canvas
  // can't read `var(--…)` directly, so the resolution has to happen
  // here regardless of which shape the author used.
  //
  // `lowColor` is always the low end of the ramp; the sequential
  // fallback is the neutral floor, the diverging fallback is the
  // brand negative so signed data reads "red → neutral → teal" out
  // of the box.
  const lowFallback = diverging ? COLORS.negative : CHART_COLORS.calendarEmpty;
  const low = resolveCssVar(props.spec.lowColor) ?? lowFallback;
  const high = resolveCssVar(props.spec.highColor) ?? COLORS.primary;
  const mid = resolveCssVar(props.spec.midColor) ?? CHART_COLORS.calendarEmpty;
  const colours = diverging ? [low, mid, high] : [low, high];
  const valueFormat = props.spec.valueFormat ?? props.formats[props.spec.value];
  return {
    type: "continuous" as const,
    min: range[0],
    max: range[1],
    calculable: false,
    orient: "horizontal" as const,
    left: "center",
    bottom: 0,
    itemWidth: 12,
    // The ramp's length shrinks on narrow charts so it and its two end
    // labels still fit the width.
    itemHeight: ctx.value.width > 0 ? Math.min(120, Math.max(60, ctx.value.width - 160)) : 120,
    textStyle: { color: chartLabelColor() },
    inRange: { color: colours },
    formatter: valueFormat
      ? (v: number) => formatValue(v, valueFormat)
      : undefined,
  };
});

const option = computed(() => {
  const base = baseOption({ title: props.spec.title }, ctx.value);
  const m = model.value;
  const valueFormat = props.spec.valueFormat ?? props.formats[props.spec.value];
  const w = ctx.value.width;
  // Heatmap columns are categorical and every column matters: show
  // every label, rotating (and truncating) only when they don't fit
  // their columns — the old fixed 30° rotation overlapped at narrow
  // widths and wasted room at wide ones. Unmeasured keeps the old
  // rotation.
  const xLabel =
    w > 0
      ? {
          interval: 0,
          hideOverlap: false,
          ...categoryLabelPolicy({
            labels: m.xCats,
            plotWidth: plotWidthEstimate(w),
            mode: "rotate",
          }),
        }
      : { interval: 0, rotate: 30, hideOverlap: false };
  // Row labels sit in the left gutter: cap their width like horizontal
  // bars; thin them only once rows get too short to label.
  const rowPx = (height.value - 110) / Math.max(1, m.yCats.length);
  const yLabel = {
    ...(w > 0 ? verticalCategoryLabel(m.yCats, w) : {}),
    ...(rowPx >= MIN_LABELLED_ROW_PX
      ? { interval: 0, hideOverlap: false }
      : { hideOverlap: true }),
  };
  return {
    ...base,
    // Tooltip needs `item` trigger for heatmaps — `axis` doesn't fire on
    // individual cells.
    tooltip: {
      ...tooltipDefaults("item", ctx.value),
      formatter: (p: { value: [number, number, number] }) => {
        const [xi, yi, v] = p.value;
        const label = valueFormat ? formatValue(v, valueFormat) : String(v);
        return `${m.xCats[xi]} · ${m.yCats[yi]}<br/>${label}`;
      },
    },
    // Room for the visualMap ramp under the plot; the grid's
    // `containLabel` already fits the x labels, rotated or not.
    grid: { ...base.grid, bottom: 36 },
    xAxis: {
      ...base.xAxis,
      type: "category" as const,
      data: m.xCats,
      // `splitArea` was previously enabled on both axes — with no
      // custom colours ECharts paints its built-in cool-grey stripe
      // pair (`rgba(250,250,250,0.3)` / `rgba(200,200,200,0.3)`) behind
      // every column AND row, layering them into a checker that clashes
      // with the warm theme and, worse, leaks through the gaps where
      // the sparse `[xi, yi, v]` grid has no cell. Off; the heatmap
      // ramp is the source of truth for grid structure.
      splitArea: { show: false },
      // ECharts' default `hideOverlap` would silently drop every Nth
      // column label — see `xLabel` above.
      axisLabel: { ...base.xAxis.axisLabel, ...xLabel },
    },
    yAxis: {
      ...base.yAxis,
      type: "category" as const,
      data: m.yCats,
      splitArea: { show: false },
      axisLabel: { ...base.yAxis.axisLabel, ...yLabel },
    },
    visualMap: visualMap.value,
    series: [
      {
        type: "heatmap" as const,
        data: m.cells,
        label: { show: false },
        // Cell borders were previously 2px in `COLORS.background` (the
        // paper token). On the ~14px legibility-floor rows that ate
        // ~30% of the coloured area per cell and left cells looking
        // washed-out even when the ramp emitted the right hex. Worse,
        // the paper tone doesn't match the card surface the chart is
        // usually mounted on, so the "invisible separator" rendered as
        // a visible grid in the wrong colour. Transparent 1px keeps
        // ECharts' hit-testing intact without painting a visible seam;
        // the ramp itself provides the between-cell contrast.
        itemStyle: { borderColor: "transparent", borderWidth: 1 },
        emphasis: {
          // Warm charcoal on hover so the emphasis ring belongs to the
          // same canvas-text family as axis labels / legend (see the
          // chart-label rationale on `COLORS.chartLabelStrong`), rather
          // than the cool `COLORS.text` used off-canvas.
          itemStyle: { borderColor: COLORS.chartLabelStrong, borderWidth: 2 },
        },
      },
    ],
    legend: { show: false },
  };
});

// Heatmaps need vertical room proportional to their y-category count
// or cells render as wide-short ribbons that read as horizontal bands.
//
// Two height regimes:
//   - **Author set `spec.height`**: respect their intent (e.g. "keep
//     this compact next to its sibling in a 2-column layout") BUT
//     enforce a per-cell legibility floor. Below ~14px per cell the
//     rows blur into a continuous band — the chart starts lying about
//     its own structure. We honour the spirit (compact) up to that
//     floor, then override.
//   - **Author left height unset**: auto-scale generously from row
//     count so a 16-hour-by-7-day grid gets near-square cells. The
//     400px cross-chart cap doesn't apply here — heatmaps with many
//     rows have to grow taller to stay legible.
//
// AI-author scar tissue: an LLM picking `height: 200` to match a
// sibling chart in a `sbi.ui.columns` doesn't know the heatmap's
// vertical density depends on y-category count. The floor here is
// what catches that misjudgement at render time.
const height = computed(() => {
  const rowCount = model.value.yCats.length || 1;
  // 80px chrome reserves room for the x-axis labels + the visualMap
  // legend under the plot. 14px/row is the
  // minimum cell height where horizontal grid lines stay visible.
  const minLegible = rowCount * 14 + 80;
  if (props.spec.height !== undefined) {
    return Math.max(props.spec.height, minLegible);
  }
  // 30px/row for breathing-room cells; clamp to 250-800 for the
  // default-render case.
  const rowDriven = rowCount * 30 + 80;
  return Math.min(Math.max(rowDriven, 250), 800);
});

// Heatmap click params carry `dataIndex` into the series data array,
// which we kept in lock-step with the original row array. Returning the
// untouched row gives authors the natural drill-down primitive.
function onClick(p: { dataIndex: number }) {
  if (!props.spec.id || !p || typeof p.dataIndex !== "number") return;
  const row = model.value.rows[p.dataIndex];
  if (!row) return;
  emit("click-target", props.spec.id, row);
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
